import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { UserPlus, X, Users } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Administrador' },
  { value: 'designer', label: 'Diseñador' },
  { value: 'procurement', label: 'Compras' },
  { value: 'logistics', label: 'Logística' },
  { value: 'client', label: 'Cliente' },
  { value: 'supplier', label: 'Proveedor' },
];
const ROLE_COLORS = {
  admin: '#C9A66B', designer: '#D4A574', procurement: '#8B9F8D',
  logistics: '#C9B8A8', client: '#B87B7B', supplier: '#A0A0A0',
};

export default function OSTeam() {
  const { authFetch, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'designer' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await authFetch('/api/auth/users');
      if (res.ok) setUsers(await res.json());
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch('/api/auth/invite', { method: 'POST', body: JSON.stringify(form) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      toast.success('Miembro invitado exitosamente');
      setShowModal(false);
      setForm({ name: '', email: '', password: '', role: 'designer' });
      load();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const isAdmin = user?.role === 'admin';

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#C9A66B', borderTopColor: 'transparent' }} /></div>;

  return (
    <div data-testid="os-team-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>Equipo</h1>
          <p className="text-sm mt-1" style={{ color: '#C9B8A8' }}>{users.length} miembro{users.length !== 1 ? 's' : ''}</p>
        </div>
        {isAdmin && (
          <button
            data-testid="invite-user-btn"
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-light tracking-wide transition-all duration-300"
            style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#C9A66B'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2D2D2D'}
          >
            <UserPlus size={16} /> Invitar
          </button>
        )}
      </div>

      {/* Users grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {users.map(u => (
          <div key={u.id} data-testid={`team-member-${u.id}`} className="rounded-xl border p-5" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium" style={{ backgroundColor: (ROLE_COLORS[u.role] || '#C9B8A8') + '20', color: ROLE_COLORS[u.role] }}>
                {u.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: '#2D2D2D' }}>{u.name}</p>
                <p className="text-xs truncate" style={{ color: '#C9B8A8' }}>{u.email}</p>
              </div>
              <span className="text-[10px] px-2 py-1 rounded-full font-medium" style={{ backgroundColor: (ROLE_COLORS[u.role] || '#C9B8A8') + '15', color: ROLE_COLORS[u.role] }}>
                {ROLES.find(r => r.value === u.role)?.label || u.role}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Invite Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="rounded-2xl border p-8 w-full max-w-md shadow-xl" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>Invitar Miembro</h2>
              <button onClick={() => setShowModal(false)} style={{ color: '#C9B8A8' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Nombre</label>
                <input data-testid="invite-field-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }} placeholder="Nombre completo" />
              </div>
              <div>
                <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Email</label>
                <input data-testid="invite-field-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }} placeholder="email@ejemplo.com" />
              </div>
              <div>
                <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Contraseña</label>
                <input data-testid="invite-field-password" type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }} placeholder="Contraseña inicial" />
              </div>
              <div>
                <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Rol</label>
                <select data-testid="invite-field-role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-lg text-sm border transition-colors" style={{ borderColor: '#E8E3D8', color: '#2D2D2D' }}>Cancelar</button>
                <button data-testid="invite-submit-btn" type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg text-sm font-medium tracking-wide transition-all disabled:opacity-50" style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}>
                  {saving ? 'Invitando...' : 'Invitar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
